import { useEffect, useRef, useState, type FormEvent } from "react";
import { createPortal } from "react-dom";
import { onEscape } from "../lib/escape.ts";
import { NoWorkersBanner } from "../components/NoWorkersBanner.tsx";
import { ArchivedBanner } from "../components/ProjectArchivedNotice.tsx";
import { Link, useParams, useSearchParams } from "react-router";
import { WorkersCard } from "../components/Workers.tsx";
import { SIZES, WORKER_FILED_WINDOW_MS, isWorkerFiling, workerFilingCutoff, type ActorDTO, type Size, type Stage, type TaskDTO } from "@roomate/core";
import { keys, useAbandonmentEvents, useActors, useCreate, useMe, useProject, useSetProjectArchived, useWorkers, type ArchivableProject } from "../api.ts";
import { AbandonedBadge, abandonmentFor, dependencyLabel } from "../abandon-merge.tsx";
import { GitBranchesPanel, GitSummary } from "../GitPanel.tsx";
import { Action, Avatar, Bento, Disclosure, Page, PageHeader, Pill, Ring, Tile } from "../calm.tsx";
import { GitGraph } from "../components/GitGraph.tsx";
import { useGitSnapshot } from "../api.ts";
import { Switch } from "../calm.tsx";
import { cx, Empty, ErrorNote, Glyph, inputCls, STAGE_LABEL } from "../ui.tsx";
import { GuardLog } from "../components/GuardLog.tsx";
import { ProjectGithub } from "../components/ProjectGithub.tsx";

export const BOARD_COLUMNS: { stage: Stage; hint: string }[] = [
  { stage: "backlog", hint: "add acceptance criteria + approve the spec" },
  { stage: "ready", hint: "claim it or assign a builder" },
  { stage: "in_progress", hint: "submit evidence to verify" },
  { stage: "blocked", hint: "answer the decision to unblock" },
  { stage: "verifying", hint: "pass → done · fail → build · unsure → review" },
  { stage: "review", hint: "a human approves, reverts, or cancels" },
  { stage: "done", hint: "verified — nothing left to do" },
  { stage: "cancelled", hint: "cancelled or deliberately abandoned" },
];

const VERDICT_LABEL = { pass: "Passed", fail: "Failed", inconclusive: "Unsure" } as const;
const VERDICT_TONE = { pass: "live", fail: "danger", inconclusive: "signal" } as const;

/** `?show=` filters from the status page's "+N more" links. */
export const BOARD_FILTERS = {
  bugs: { label: "Open bugs", test: (t: TaskDTO) => t.kind === "bug" && t.stage !== "done" && t.stage !== "cancelled" },
  blocked: { label: "Blocked", test: (t: TaskDTO) => t.stage === "blocked" },
  merge_failed: { label: "Failed merges", test: (t: TaskDTO) => t.mergeState === "merge_failed" },
  shipped: { label: "Shipped", test: (t: TaskDTO) => t.mergeState === "merged" },
  in_progress: { label: "In progress", test: (t: TaskDTO) => t.stage === "in_progress" || t.stage === "verifying" || t.stage === "review" },
} as const;
export type BoardFilter = keyof typeof BOARD_FILTERS;

/** The board keeps cancelled work out of sight (spec: cancelled is terminal for the board), except abandoned merges, which stay visible with their badge. */
export const tasksInBoardColumn = (tasks: readonly TaskDTO[], stage: Stage) =>
  tasks.filter((task) => task.stage === stage && (stage !== "cancelled" || task.mergeState === "abandoned"));

type BoardRow = { labels: string[]; createdAt: number; stage: Stage };

/**
 * Cutoff for `?since=`. `7d` uses `now` (the hub clock once the page has it). A numeric value is
 * that cutoff itself, so the board does not recompute the window on the browser clock.
 */
export function filedWindowStart(since: string | null, now: number): number | null {
  if (since === "7d") return workerFilingCutoff(now);
  if (since && /^\d+$/.test(since)) {
    const n = Number(since);
    return Number.isSafeInteger(n) ? n : null;
  }
  return null;
}

/** Chip text for an active label and/or window. Null when neither narrows the board. */
export function boardFilterChipText(label: string | null, since: string | null, count: number): string | null {
  const windowed = !!since && filedWindowStart(since, 0) !== null;
  if (!label && !windowed) return null;
  const parts = [label ? `label ${label}` : null, windowed ? "last 7d" : null].filter(Boolean);
  return `${parts.join(" · ")} · ${count} ✕`;
}

/**
 * Narrows the board by `?label=` and, separately, `?since=`. The 7-day window is not implied by a
 * label: older labelled tasks stay visible unless the link asked for the window.
 */
export function tasksForLabel<T extends BoardRow>(tasks: T[], label: string | null, since: number | null): T[] {
  if (!label && since === null) return tasks;
  return tasks.filter((t) => (!label || t.labels.includes(label)) && (since === null || t.createdAt >= since));
}

/** Status-page `?show=` and the label/since params, applied together. */
export function filteredBoardTasks<T extends BoardRow>(tasks: T[], show: ((task: T) => boolean) | null, label: string | null, since: number | null): T[] {
  return tasksForLabel(show ? tasks.filter(show) : tasks, label, since);
}

/** Project board URL. Omitting `show` is "Show all": the label and since params stay. */
export function projectBoardPath(projectId: string, query: { show?: string | null; label?: string | null; since?: string | null }): string {
  const next = new URLSearchParams();
  if (query.show) next.set("show", query.show);
  if (query.label) next.set("label", query.label);
  if (query.since) next.set("since", query.since);
  const q = next.toString();
  return `/projects/${projectId}${q ? `?${q}` : ""}`;
}

/** Rows the board actually draws. Cancelled has no column; done hides behind the switch. */
export function visibleBoardTasks<T extends BoardRow>(tasks: T[], showDone: boolean): T[] {
  return tasks.filter((t) => t.stage !== "cancelled" && (showDone || t.stage !== "done"));
}

export function ProjectPage() {
  const { id = "" } = useParams();
  const project = useProject(id);
  const workers = useWorkers(id);
  const actors = useActors().data;
  const events = useAbandonmentEvents(project.data?.tasks ?? []);
  const [showDone, setShowDone] = useState(true);
  const isAdmin = useMe().data?.access === "admin";
  const [archiving, setArchiving] = useState(false);
  const [params, setParams] = useSearchParams();
  const showParam = params.get("show");
  const filter = showParam && showParam in BOARD_FILTERS ? BOARD_FILTERS[showParam as BoardFilter] : null;
  const tab = params.get("tab") === "guard" ? "guard" : "board";
  const label = params.get("label");
  const sinceParam = params.get("since");
  if (project.error) return <ErrorNote error={project.error} />;
  if (!project.data)
    return (
      <Page>
        <p className="text-ink-3">Loading…</p>
      </Page>
    );
  const { status, intents } = project.data;
  const allTasks = project.data.tasks;
  // `?show=` (status page) and `?label=` / `?since=` (Workers card) both narrow the board only. Header stats stay on the whole project.
  // `since=7d` uses the hub cutoff from the workers card once it has loaded. A numeric `since` is that cutoff.
  const hubCutoff = workers.data?.[0]?.filedSince;
  const sinceStart = sinceParam === "7d" && hubCutoff != null ? hubCutoff : filedWindowStart(sinceParam, Date.now());
  const boardTasks = filteredBoardTasks(allTasks, filter?.test ?? null, label, sinceStart);
  const workerKey = label?.startsWith("worker:") ? label.slice("worker:".length) : null;
  // Columns follow `isWorkerFiling` whenever the link asked for a worker's 7-day window, so the
  // chip count and `filed7d` share one predicate. Cancelled rows stay on `boardTasks` for the footnote.
  const columnTasks =
    workerKey && sinceStart !== null ? boardTasks.filter((t) => isWorkerFiling(t, workerKey, sinceStart + WORKER_FILED_WINDOW_MS)) : boardTasks;
  const visible = visibleBoardTasks(columnTasks, showDone);
  const filterChip = boardFilterChipText(label, sinceStart !== null ? (sinceParam ?? "7d") : null, visible.length);
  const clearLabel = () => {
    const next = new URLSearchParams(params);
    next.delete("label");
    next.delete("since");
    setParams(next);
  };
  const p = status.project;
  const archivedAt = (p as ArchivableProject).archivedAt ?? null;
  // The summary tiles always describe the whole project; only the board below is filtered.
  const live = allTasks.filter((t) => t.stage !== "cancelled");
  const done = live.filter((t) => t.stage === "done").length;
  const ready = allTasks.filter((t) => t.stage === "ready").length;
  const building = allTasks.filter((t) => t.stage === "in_progress").length;
  const review = allTasks.filter((t) => t.stage === "review").length;
  const cancelled = boardTasks.filter((t) => t.stage === "cancelled" && t.mergeState !== "abandoned").length;

  return (
    <Page wide>
      <PageHeader
        crumbs={[{ label: "Overview", to: "/" }]}
        title={p.name}
        meta={
          <>
            <span className="capitalize">{p.kind}</span>
            {p.description && <span className="max-w-[48ch] truncate text-ink-2">{p.description}</span>}
            {p.repoUrl && (
              <a href={p.repoUrl} target="_blank" rel="noreferrer" className="text-ink-3 underline decoration-rule hover:text-ink">
                Repository ↗
              </a>
            )}
            {p.mergeBacklog && (p.mergeBacklog.queued > 0 || p.mergeBacklog.cap !== null) && (
              <Pill tone={p.mergeBacklog.cap !== null && p.mergeBacklog.queued >= p.mergeBacklog.cap ? "danger" : "neutral"} testId="merge-queue-chip">
                Merge queue {p.mergeBacklog.queued}
                {p.mergeBacklog.cap !== null ? `/${p.mergeBacklog.cap}` : ""}
              </Pill>
            )}
          </>
        }
        actions={
          <>
            <Link to={`/projects/${p.id}/lessons`} data-testid="project-lessons-link" className="rounded-full px-4 py-2 text-[13.5px] text-ink-2 transition-colors hover:bg-ink/[0.06] hover:text-ink">
              Lessons
            </Link>
            {!archivedAt && <NewIntent projectId={p.id} />}
            {!archivedAt && <NewTask projectId={p.id} />}
            {isAdmin && !archivedAt && (
              <Action onClick={() => setArchiving(true)} testId="archive-project">
                Archive project
              </Action>
            )}
          </>
        }
      />
      {!archivedAt && <NoWorkersBanner className="mb-6" />}
      {!archivedAt && <ProjectGithub project={p} />}
      {archivedAt && <ArchivedBanner projectId={p.id} archivedAt={archivedAt} isAdmin={isAdmin} />}
      {archiving && <ArchiveProjectDialog projectId={p.id} name={p.name} onClose={() => setArchiving(false)} />}

      <nav className="mt-8 flex gap-1 border-b border-rule" aria-label="Project views">
        {(["board", "guard"] as const).map((t) => (
          <Link
            key={t}
            to={t === "board" ? `/projects/${p.id}` : `/projects/${p.id}?tab=guard`}
            aria-current={tab === t ? "page" : undefined}
            data-testid={`project-tab-${t}`}
            className={cx("border-b-2 px-3 py-2 text-[13px]", tab === t ? "border-ink text-ink" : "border-transparent text-ink-3 hover:text-ink-2")}
          >
            {t === "board" ? "Board" : "Guard"}
          </Link>
        ))}
      </nav>

      {tab === "guard" ? (
        <Bento className="mt-6">
          <GuardLog projectId={p.id} tasks={allTasks} />
        </Bento>
      ) : (
      <Bento className="mt-6">
        <Tile span={2} title="Work">
          <div className="flex items-center gap-5">
            <Ring value={done} total={live.length} tone={done === live.length && done > 0 ? "ok" : "ink"} />
            <div className="min-w-0">
              <p className="text-[15px] font-medium leading-snug">{live.length ? `${done} of ${live.length} shipped` : "No tasks yet"}</p>
              <p className="mt-1 text-[12.5px] text-ink-2">
                {[ready && `${ready} ready`, building && `${building} building`, review && `${review} in review`].filter(Boolean).join(" · ") || "Nothing in flight"}
              </p>
            </div>
          </div>
        </Tile>

        <Tile span={4} title="Git">
          <div data-testid="project-git-summary">
            <GitSummary projectId={p.id} />
          </div>
        </Tile>

        {intents.length > 0 && (
          <Tile span={6} title="Intents" aside={`${intents.length}`}>
            <ul className="flex flex-wrap gap-2">
              {intents.map((i) => (
                <li key={i.id}>
                  <Link
                    to={`/intents/${i.id}`}
                    className="inline-flex items-center gap-2 rounded-full bg-ink/[0.05] py-1.5 pl-1.5 pr-3.5 text-[13px] hover:bg-ink/[0.09]"
                  >
                    <Pill tone={i.status === "approved" ? "live" : i.status === "spec_review" ? "signal" : "neutral"}>{i.status.replace("_", " ")}</Pill>
                    {i.title}
                  </Link>
                </li>
              ))}
            </ul>
          </Tile>
        )}

        <Tile
          span={6}
          title="Board"
          aside={
            <span className="inline-flex items-center gap-3">
              {filterChip && (
                <button type="button" data-testid="board-label-filter" aria-label={label ? `Clear label filter ${label}` : "Clear time filter"} onClick={clearLabel} className="rounded-full bg-ink/[0.07] px-2.5 py-0.5 text-[12px] text-ink-2 hover:bg-ink/[0.12]">
                  {filterChip}
                </button>
              )}
              <Switch checked={showDone} onChange={setShowDone} label="Show done" />
            </span>
          }
        >
          {filter && (
            <p data-testid="board-filter" className="mb-3 text-[12.5px] text-ink-2">
              Showing {filter.label.toLowerCase()} ({boardTasks.length}).{" "}
              <Link to={projectBoardPath(p.id, { label, since: sinceParam })} className="underline hover:text-ink">
                Show all
              </Link>
            </p>
          )}
          <div className="-mx-5 overflow-x-auto px-5 pb-1 md:-mx-6 md:px-6">
            <div className="flex gap-3">
              {BOARD_COLUMNS.filter((c) => showDone || c.stage !== "done").map((col) => {
                const items = tasksInBoardColumn(columnTasks, col.stage);
                if (col.stage === "cancelled" && items.length === 0) return null;
                return (
                  <section key={col.stage} data-column={col.stage} aria-label={`${STAGE_LABEL[col.stage]}: ${col.hint}`} className="w-[230px] shrink-0 rounded-2xl bg-ink/[0.03] p-3">
                    <div className="mb-1.5 flex items-baseline justify-between px-1" title={col.hint}>
                      <h3 className="text-[13px] font-medium">{STAGE_LABEL[col.stage]}</h3>
                      <span className="text-[12px] text-ink-3">{items.length}</span>
                    </div>
                    <ol className="space-y-0.5">
                      {items.map((t) => (
                        <li key={t.id}>
                          <TaskRow task={t} tasks={allTasks} events={events} actors={actors} />
                        </li>
                      ))}
                    </ol>
                    {items.length === 0 && <p className="px-1 py-4 text-center text-[12px] text-ink-3">—</p>}
                  </section>
                );
              })}
            </div>
          </div>
          {cancelled > 0 && (
            <p className="mt-3 text-[12.5px] text-ink-3">
              {cancelled} cancelled task{cancelled === 1 ? "" : "s"} hidden.
            </p>
          )}
        </Tile>

        <WorkersCard projectId={p.id} />

        <HistoryTile projectId={p.id} />
      </Bento>
      )}
    </Page>
  );
}

export function TaskRow({ task, tasks, events, actors }: { task: TaskDTO; tasks: readonly TaskDTO[]; events: Parameters<typeof abandonmentFor>[1]; actors: ActorDTO[] | undefined }) {
  const builder = task.builderId ? actors?.find((a) => a.id === task.builderId) : undefined;
  const suggested = !builder && task.suggestion?.builderId ? actors?.find((a) => a.id === task.suggestion!.builderId) : undefined;
  const abandonment = abandonmentFor(task, events, actors ?? []);
  const isAbandoned = task.mergeState === "abandoned";
  const dependency = dependencyLabel(task, tasks);
  const pill = isAbandoned
    ? null
    : task.verification
    ? { tone: VERDICT_TONE[task.verification.verdict], label: VERDICT_LABEL[task.verification.verdict] }
    : task.priority === "urgent" || task.priority === "high"
      ? { tone: "signal" as const, label: task.priority === "urgent" ? "Urgent" : "High" }
      : null;
  return (
    <Link to={`/tasks/${task.id}`} data-task={task.number} className="flex items-center gap-2.5 rounded-xl px-2 py-2 transition-colors hover:bg-ink/[0.06]">
      {builder ? (
        <Avatar name={builder.name} kind={builder.kind} size={28} live={!!builder.online} />
      ) : (
        <span aria-hidden className="h-7 w-7 shrink-0 rounded-full border-2 border-dashed border-rule" />
      )}
      <span className="min-w-0 flex-1">
        <span className="block truncate text-[13px] font-medium leading-snug">{task.title}</span>
        <span className="block truncate text-[11px] text-ink-3">
          #{task.number} · {builder ? builder.name : suggested ? `suggested ${suggested.name}` : "unassigned"}
        </span>
        {dependency && <span className={cx("block truncate text-[11px]", dependency.startsWith("Blocked:") ? "text-high" : "text-ink-3")} data-testid="dependency-chip">{dependency}</span>}
      </span>
      {isAbandoned && <AbandonedBadge abandonment={abandonment} />}
      {pill && (
        <span className="shrink-0">
          <Pill tone={pill.tone}>{pill.label}</Pill>
        </span>
      )}
    </Link>
  );
}

function splitLines(s: string) {
  return s
    .split("\n")
    .map((x) => x.trim())
    .filter(Boolean);
}

function NewTask({ projectId }: { projectId: string }) {
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [criteria, setCriteria] = useState("");
  const [areas, setAreas] = useState("");
  const [size, setSize] = useState<Size>("s");
  const create = useCreate<TaskDTO>("/tasks", [keys.project(projectId), keys.overview, keys.tasks]);
  const submit = (e: FormEvent) => {
    e.preventDefault();
    create.mutate(
      { projectId, title, description, acceptanceCriteria: splitLines(criteria), areas: splitLines(areas), size },
      {
        onSuccess: () => {
          setOpen(false);
          setTitle("");
          setDescription("");
          setCriteria("");
          setAreas("");
        },
      },
    );
  };
  return (
    <>
      <Action primary onClick={() => setOpen(true)}>
        + Task
      </Action>
      {open && (
        <Dialog title="New task" onClose={() => setOpen(false)}>
          <form onSubmit={submit} className="space-y-3">
            <Field label="Title">
              <input className={inputCls} value={title} onChange={(e) => setTitle(e.target.value)} autoFocus />
            </Field>
            <Field label="Description">
              <textarea className={inputCls} rows={3} value={description} onChange={(e) => setDescription(e.target.value)} />
            </Field>
            <Field label="Acceptance criteria" hint="one per line; required before the task can be routed">
              <textarea className={cx(inputCls, "font-mono text-[12.5px]")} rows={3} value={criteria} onChange={(e) => setCriteria(e.target.value)} />
            </Field>
            <Field label="Areas" hint="paths or globs it touches, one per line (drives risk, rules and overlap detection)">
              <textarea className={cx(inputCls, "font-mono text-[12.5px]")} rows={2} placeholder="apps/api/src/invoices/**" value={areas} onChange={(e) => setAreas(e.target.value)} />
            </Field>
            <Field label="Size">
              <div className="flex gap-1.5">
                {SIZES.map((s) => (
                  <button
                    type="button"
                    key={s}
                    onClick={() => setSize(s)}
                    className={cx("rounded-full px-3 py-1 text-[12.5px] transition-colors", size === s ? "bg-ink text-paper" : "bg-ink/[0.05] text-ink-2 hover:bg-ink/[0.09]")}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </Field>
            <ErrorNote error={create.error} />
            <div className="flex justify-end gap-2 pt-1">
              <Action onClick={() => setOpen(false)}>Cancel</Action>
              <Action primary type="submit" disabled={!title.trim() || create.isPending}>
                Create and route
              </Action>
            </div>
          </form>
        </Dialog>
      )}
    </>
  );
}

function NewIntent({ projectId }: { projectId: string }) {
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [problem, setProblem] = useState("");
  const [kind, setKind] = useState<"feature" | "bug" | "chore">("feature");
  const create = useCreate<{ id: string }>("/intents", [keys.project(projectId)]);
  const submit = (e: FormEvent) => {
    e.preventDefault();
    create.mutate(
      { projectId, title, problem, kind },
      {
        onSuccess: (intent) => {
          setOpen(false);
          location.assign(`/intents/${intent.id}`);
        },
      },
    );
  };
  return (
    <>
      <Action onClick={() => setOpen(true)}>+ Intent</Action>
      {open && (
        <Dialog title="New intent" onClose={() => setOpen(false)}>
          <form onSubmit={submit} className="space-y-3">
            <Field label="What do we want?">
              <input className={inputCls} value={title} onChange={(e) => setTitle(e.target.value)} autoFocus />
            </Field>
            <Field label="Kind">
              <select className={inputCls} value={kind} onChange={(e) => setKind(e.target.value as typeof kind)}>
                <option>feature</option>
                <option>bug</option>
                <option>chore</option>
              </select>
            </Field>
            <Field label="Problem" hint="who hurts, and how do we know">
              <textarea className={inputCls} rows={4} value={problem} onChange={(e) => setProblem(e.target.value)} />
            </Field>
            <ErrorNote error={create.error} />
            <div className="flex justify-end pt-1">
              <Action primary type="submit" disabled={!title.trim() || create.isPending}>
                Create, then write the spec
              </Action>
            </div>
          </form>
        </Dialog>
      )}
    </>
  );
}

export function Field({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="text-[13px] font-medium text-ink-2">{label}</span>
      {hint && <span className="ml-2 text-[12px] text-ink-3">{hint}</span>}
      <div className="mt-1.5">{children}</div>
    </label>
  );
}

/** The dialog box itself (no portal). The header sticks so the title and close button stay reachable when a tall body scrolls. */
export function DialogPanel({ title, onClose, children }: { title: string; onClose: () => void; children: React.ReactNode }) {
  return (
    <div role="dialog" aria-modal="true" aria-label={title} className="glass max-h-[calc(100dvh-2rem)] w-full max-w-lg overflow-y-auto rounded-[24px] p-6 shadow-2xl">
      <div data-testid="dialog-header" className="sticky top-0 z-10 -mx-6 -mt-6 mb-5 flex items-center justify-between rounded-t-[24px] bg-card/90 px-6 pb-3 pt-6 backdrop-blur">
        <h2 className="text-[20px] font-semibold tracking-[-0.01em]">{title}</h2>
        <button onClick={onClose} aria-label="Close" className="grid h-7 w-7 place-items-center rounded-full text-ink-3 hover:bg-ink/[0.07] hover:text-ink">
          ✕
        </button>
      </div>
      {children}
    </div>
  );
}

export function Dialog({ title, onClose, children }: { title: string; onClose: () => void; children: React.ReactNode }) {
  // Escape is heard on window: once focus falls to <body> (e.g. the textarea is replaced by a
  // confirmation view) an overlay-level onKeyDown never fires.
  const closeRef = useRef(onClose);
  useEffect(() => {
    closeRef.current = onClose;
  });
  useEffect(() => onEscape(window, () => closeRef.current()), []);
  // Portalled to <body>: a modal rendered inline can end up a descendant of an animated (`os-rise`)
  // ancestor, and any CSS transform on an ancestor rescopes `position: fixed` to that ancestor's
  // box instead of the viewport, breaking the overlay's sizing and click targeting.
  return createPortal(
    <div className="fixed inset-0 z-50 grid place-items-center bg-ink/30 p-4 backdrop-blur-[2px]" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <DialogPanel title={title} onClose={onClose}>
        {children}
      </DialogPanel>
    </div>,
    document.body,
  );
}

export { Glyph, Empty };

/** Admin confirm before archiving: nothing is deleted, the project only stops taking new work. */
export function ArchiveProjectDialog({ projectId, name, onClose }: { projectId: string; name: string; onClose: () => void }) {
  const archive = useSetProjectArchived();
  return (
    <Dialog title="Archive project" onClose={onClose}>
      <div className="space-y-3" data-testid="archive-project-dialog">
        <p className="text-[13.5px] text-ink-2">
          Archive <span className="font-medium text-ink">{name}</span>? It leaves the project list, Bridge and Inbox and takes no new work. Nothing is deleted, and an admin can unarchive it from Projects.
        </p>
        <ErrorNote error={archive.error} />
        <div className="flex justify-end gap-2 pt-1">
          <Action onClick={onClose} testId="archive-project-cancel">
            Cancel
          </Action>
          <Action primary disabled={archive.isPending} testId="archive-project-confirm" onClick={() => archive.mutate({ id: projectId, archived: true }, { onSuccess: onClose })}>
            Archive project
          </Action>
        </div>
      </div>
    </Dialog>
  );
}

/** The repo's history as a graph (lanes, merges, branch chips); the full branch table is one click away. */
function HistoryTile({ projectId }: { projectId: string }) {
  const snap = useGitSnapshot(projectId).data;
  const taskByBranch = new Map((snap?.branches ?? []).filter((b) => b.taskId).map((b) => [b.name, b.taskId!]));
  return (
    <Tile span={6} title="History" aside={snap?.main.sha ? <span className="font-mono">main {snap.main.sha.slice(0, 7)}</span> : undefined}>
      {snap?.graph?.length ? <GitGraph commits={snap.graph} taskByBranch={taskByBranch} /> : <p className="text-[13px] text-ink-3">The graph appears once a node reports this checkout.</p>}
      <Disclosure summary="All branches" className="mt-4">
        <GitBranchesPanel projectId={projectId} />
      </Disclosure>
    </Tile>
  );
}
